import { adoptResponseHeaders, normalizeResponse, requestHeaders } from './fetchShared.js';
export function createAbortError() {
  const err = new Error('The request was aborted.');
  err.name = 'AbortError';
  return err;
}
export function isAbortError(err) {
  return !!err && typeof err === 'object' && err.name === 'AbortError';
}
function transportFailure() {
  return {
    status: 0,
    data: null
  };
}
function parseLine(raw) {
  const line = raw.charAt(raw.length - 1) === '\r' ? raw.slice(0, -1) : raw;
  if (!line.trim()) return {
    skip: true
  };
  try {
    return {
      value: JSON.parse(line)
    };
  } catch (e) {
    return {
      bad: true
    };
  }
}
function drain(buffer, out) {
  let start = 0;
  let at = buffer.indexOf('\n', start);
  while (at !== -1) {
    const parsed = parseLine(buffer.slice(start, at));
    if (parsed.bad) throw transportFailure();
    if (!parsed.skip) out.push(parsed.value);
    start = at + 1;
    at = buffer.indexOf('\n', start);
  }
  return buffer.slice(start);
}
function makeDecoder() {
  if (typeof TextDecoder === 'function') {
    const decoder = new TextDecoder('utf-8');
    return {
      push: chunk => typeof chunk === 'string' ? chunk : decoder.decode(chunk, {
        stream: true
      }),
      end: () => decoder.decode()
    };
  }
  return {
    push: chunk => {
      if (typeof chunk === 'string') return chunk;
      throw transportFailure();
    },
    end: () => ''
  };
}
export function createFetchStreamer(opts) {
  const options = opts || {};
  const baseUrl = options.baseUrl || '';
  const headerProvider = options.headerProvider || null;
  const fetchImplOpt = options.fetchImpl || null;
  async function* streamLines(req, streamOpts) {
    const signal = streamOpts && streamOpts.signal || null;
    const aborted = () => !!(signal && signal.aborted);
    if (aborted()) throw createAbortError();
    let url = req.path;
    if (url.indexOf('/api') === 0) url = baseUrl + url;
    let headers;
    try {
      headers = await requestHeaders(headerProvider, false, {
        Accept: 'application/x-ndjson'
      }, req.headers);
    } catch (e) {
      throw transportFailure();
    }
    if (aborted()) throw createAbortError();
    const init = {
      method: req.method || 'GET',
      headers: headers
    };
    if (signal) init.signal = signal;
    let res;
    try {
      res = await (fetchImplOpt || fetch)(url, init);
    } catch (e) {
      if (aborted() || isAbortError(e)) throw createAbortError();
      throw transportFailure();
    }
    if (!(res.status >= 200 && res.status < 300)) {
      try {
        await normalizeResponse(headerProvider, res, true);
      } catch (rejection) {
        if (aborted()) throw createAbortError();
        throw rejection;
      }
      throw transportFailure();
    }
    await adoptResponseHeaders(headerProvider, res);
    const decoder = makeDecoder();
    let buffer = '';
    const ready = [];
    const body = res.body;
    if (!body || typeof body.getReader !== 'function') {
      let text;
      try {
        text = await res.text();
      } catch (e) {
        if (aborted() || isAbortError(e)) throw createAbortError();
        throw transportFailure();
      }
      const rest = drain(text, ready);
      for (const value of ready) yield value;
      const last = parseLine(rest);
      if (last.bad) throw transportFailure();
      if (!last.skip) yield last.value;
      return;
    }
    const reader = body.getReader();
    let finished = false;
    try {
      for (;;) {
        let step;
        try {
          step = await reader.read();
        } catch (e) {
          if (aborted() || isAbortError(e)) throw createAbortError();
          throw transportFailure();
        }
        if (step.done) break;
        buffer = drain(buffer + decoder.push(step.value), ready);
        while (ready.length) {
          yield ready.shift();
          if (aborted()) throw createAbortError();
        }
      }
      finished = true;
      buffer = drain(buffer + decoder.end(), ready);
      while (ready.length) yield ready.shift();
      const last = parseLine(buffer);
      if (last.bad) throw transportFailure();
      if (!last.skip) yield last.value;
    } finally {
      if (!finished) {
        try {
          const cancelled = reader.cancel();
          if (cancelled && typeof cancelled.catch === 'function') cancelled.catch(function () {});
        } catch (e) {}
      }
    }
  }
  return {
    streamLines: streamLines
  };
}